import { useState } from 'react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';

const revenueData: Array<{
  month: string; revenue: number; expenses: number; profit: number; cogs: number;
}> = [];

const totalRevenue = revenueData.reduce((sum, d) => sum + d.revenue, 0);
const totalExpenses = revenueData.reduce((sum, d) => sum + d.expenses, 0);
const totalProfit = revenueData.reduce((sum, d) => sum + d.profit, 0);
const maxRevenue = revenueData.length > 0 ? Math.max(...revenueData.map(d => d.revenue)) : 0;

const DEPT_PL: Array<{
  dept: string; revenue: number; expenses: number; headcount: number;
}> = [];

export function FinancialReports() {
  const [activeTab, setActiveTab] = useState<'pl' | 'dept' | 'cashflow' | 'management'>('pl');
  const [period, setPeriod] = useState('H1 2026');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Financial <span className="text-foreground">Reports</span></h1>
          <p className="text-muted-foreground mt-1">P&L · Balance Sheet · Cash Flow · Management Reports</p>
        </div>
        <div className="flex gap-3">
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="bg-background border border-border rounded-lg px-4 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
            <option>H1 2026</option><option>Q1 2026</option><option>FY 2025</option>
          </select>
          <Button variant="secondary" onClick={() => toast.success('Exporting PDF...')}>Export PDF</Button>
          <Button variant="secondary" onClick={() => toast.success('Exporting Excel...')}>Export Excel</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString()}`, sub: '↑ 12% vs prev period', color: 'text-success' },
          { label: 'Total Expenses', value: `$${totalExpenses.toLocaleString()}`, sub: '↑ 5% vs prev period', color: 'text-destructive' },
          { label: 'Net Profit', value: `$${totalProfit.toLocaleString()}`, sub: `${Math.round((totalProfit / totalRevenue) * 100)}% margin ↑ 18%`, color: 'text-primary' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-6">
            <p className="text-xs text-muted-foreground font-semibold mb-2">{s.label} ({period})</p>
            <p className={`text-4xl font-semibold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground mt-2">{s.sub}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: 'pl', label: 'Income Statement (P&L)' },
          { id: 'cashflow', label: 'Cash Flow Statement' },
          { id: 'dept', label: 'Department P&L' },
          { id: 'management', label: 'KPI Dashboard' },
        ].map(t => (
          <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
            className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </Button>
        ))}
      </div>

      {/* P&L */}
      {activeTab === 'pl' && (
        <div className="space-y-6">
          {/* Bar Chart */}
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4 flex flex-row justify-between items-center">
              <h2 className="text-foreground font-display text-section-title">Revenue vs Profit Trend</h2>
              <div className="flex gap-4 text-xs font-semibold">
                <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-primary rounded-sm inline-block" /> Revenue</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-success rounded-sm inline-block" /> Profit</span>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-end gap-4 h-52">
                {revenueData.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex gap-1 items-end" style={{ height: '180px' }}>
                      <div className="flex-1 bg-primary/70 rounded-t-md hover:bg-primary/90 transition-colors" style={{ height: `${(d.revenue / maxRevenue) * 100}%` }} title={`Revenue: $${d.revenue.toLocaleString()}`} />
                      <div className="flex-1 bg-success/70 rounded-t-md hover:bg-success/90 transition-colors" style={{ height: `${(d.profit / maxRevenue) * 100}%` }} title={`Profit: $${d.profit.toLocaleString()}`} />
                    </div>
                    <span className="text-xs font-semibold text-muted-foreground">{d.month}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* P&L Table */}
          <Card className="font-mono text-sm max-w-2xl mx-auto">
            <CardHeader className="bg-card/30 px-8 py-4 text-center">
              <h2 className="text-foreground font-display text-section-title">INCOME STATEMENT (P&L)</h2>
              <p className="text-muted-foreground text-xs mt-1">{period}</p>
            </CardHeader>
            <CardContent className="p-8">
              {[
                { label: 'REVENUE', header: true },
                { label: 'Product Sales', value: 568000 },
                { label: 'Service Revenue', value: 392000 },
                { label: 'Subscription Revenue', value: 183000 },
                { label: 'Total Revenue', value: totalRevenue, bold: true },
                { spacer: true },
                { label: 'COST OF REVENUE', header: true },
                { label: 'Direct Materials', value: 185000, neg: true },
                { label: 'Direct Labor', value: 174000, neg: true },
                { label: 'Gross Profit', value: totalRevenue - 359000, bold: true },
                { spacer: true },
                { label: 'OPERATING EXPENSES', header: true },
                { label: 'Salaries & Benefits', value: 240000, neg: true },
                { label: 'Marketing & Advertising', value: 54000, neg: true },
                { label: 'Rent & Utilities', value: 86400, neg: true },
                { label: 'Professional Services', value: 38000, neg: true },
                { label: 'Depreciation', value: 30000, neg: true },
                { label: 'Operating Income (EBIT)', value: totalProfit - 48600, bold: true },
                { spacer: true },
                { label: 'Other Income / Expense', header: true },
                { label: 'Interest Income', value: 8200 },
                { label: 'Interest Expense', value: 14400, neg: true },
                { label: 'NET PROFIT', value: totalProfit, total: true },
              ].map((row: any, i) => (
                row.spacer ? <div key={i} className="py-2" /> :
                  <div key={i} className={`flex justify-between py-1 ${row.header ? 'mt-3 text-muted-foreground text-xs' : ''} ${row.bold ? 'border-t border-border mt-1 pt-1 font-semibold text-foreground' : 'text-muted-foreground'} ${row.total ? 'border-t-2 border-double border-text-100 mt-2 pt-2 font-semibold text-foreground text-base' : ''}`}>
                    <span className={row.header ? '' : 'pl-4'}>{row.label}</span>
                    {row.value !== undefined && (
                      <span className={row.neg ? 'text-destructive' : ''}>
                        {row.neg ? `($${row.value.toLocaleString()})` : `$${row.value.toLocaleString()}`}
                      </span>
                    )}
                  </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cash Flow */}
      {activeTab === 'cashflow' && (
        <Card className="font-mono text-sm max-w-2xl mx-auto">
          <CardHeader className="bg-card/30 px-8 py-4 text-center">
            <h2 className="text-foreground font-display text-section-title">CASH FLOW STATEMENT</h2>
            <p className="text-muted-foreground text-xs mt-1">{period}</p>
          </CardHeader>
          <CardContent className="p-8 space-y-2">
            {[
              { label: 'OPERATING ACTIVITIES', header: true },
              { label: 'Net Income', value: totalProfit },
              { label: 'Depreciation & Amortization', value: 30000 },
              { label: 'Changes in Receivables', value: -14000, neg: true },
              { label: 'Changes in Payables', value: 8200 },
              { label: 'Net Cash from Operations', value: totalProfit + 24200, bold: true },
              { spacer: true },
              { label: 'INVESTING ACTIVITIES', header: true },
              { label: 'Capital Expenditures', value: -45000, neg: true },
              { label: 'Equipment Purchases', value: -18000, neg: true },
              { label: 'Net Cash from Investing', value: -63000, bold: true, neg: true },
              { spacer: true },
              { label: 'FINANCING ACTIVITIES', header: true },
              { label: 'Loan Repayments', value: -24000, neg: true },
              { label: 'Dividends Paid', value: -20000, neg: true },
              { label: 'Net Cash from Financing', value: -44000, bold: true, neg: true },
              { spacer: true },
              { label: 'NET CHANGE IN CASH', value: totalProfit + 24200 - 63000 - 44000, total: true },
              { label: 'Opening Cash Balance', value: 143000 },
              { label: 'Closing Cash Balance', value: totalProfit + 24200 - 63000 - 44000 + 143000, bold: true },
            ].map((row: any, i) => (
              row.spacer ? <div key={i} className="py-1" /> :
                <div key={i} className={`flex justify-between py-1 ${row.header ? 'mt-3 text-muted-foreground text-xs' : ''} ${row.bold ? 'border-t border-border mt-1 pt-1 font-semibold text-foreground' : 'text-muted-foreground'} ${row.total ? 'border-t-2 border-double border-text-100 mt-2 pt-2 font-semibold text-foreground text-base' : ''}`}>
                  <span className={row.header ? '' : 'pl-4'}>{row.label}</span>
                  {row.value !== undefined && (
                    <span className={row.neg && row.value < 0 ? 'text-destructive' : 'text-foreground'}>
                      {row.value < 0 ? `($${Math.abs(row.value).toLocaleString()})` : `$${row.value.toLocaleString()}`}
                    </span>
                  )}
                </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Department P&L */}
      {activeTab === 'dept' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                  <th className="text-left px-6 py-3">Department</th>
                  <th className="text-right px-4 py-3">Revenue</th>
                  <th className="text-right px-4 py-3">Expenses</th>
                  <th className="text-right px-4 py-3">Contribution</th>
                  <th className="text-right px-4 py-3">Margin</th>
                  <th className="text-center px-4 py-3">Headcount</th>
                  <th className="text-right px-4 py-3">Revenue/Head</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-12">
                {DEPT_PL.map((dept) => {
                  const contribution = dept.revenue - dept.expenses;
                  const margin = dept.revenue > 0 ? ((contribution / dept.revenue) * 100) : null;
                  return (
                    <tr key={dept.dept} className="hover:bg-card/30 transition-colors">
                      <td className="px-6 py-4 font-semibold text-foreground">{dept.dept}</td>
                      <td className="px-4 py-4 text-right font-mono text-success">{dept.revenue ? `$${dept.revenue.toLocaleString()}` : '—'}</td>
                      <td className="px-4 py-4 text-right font-mono text-destructive">${dept.expenses.toLocaleString()}</td>
                      <td className={`px-4 py-4 text-right font-mono font-semibold ${contribution >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {contribution >= 0 ? `$${contribution.toLocaleString()}` : `($${Math.abs(contribution).toLocaleString()})`}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {margin !== null ? (
                          <span className={`font-semibold ${margin > 40 ? 'text-success' : margin > 20 ? 'text-warning' : 'text-destructive'}`}>{margin.toFixed(1)}%</span>
                        ) : <span className="text-muted-foreground">N/A</span>}
                      </td>
                      <td className="px-4 py-4 text-center text-muted-foreground">{dept.headcount}</td>
                      <td className="px-4 py-4 text-right font-mono text-muted-foreground">
                        {dept.revenue ? `$${Math.round(dept.revenue / dept.headcount).toLocaleString()}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* KPI Dashboard */}
      {activeTab === 'management' && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { label: 'Gross Margin', value: '40.8%', target: '42%', good: true },
            { label: 'EBITDA Margin', value: '23.5%', target: '25%', good: false },
            { label: 'AR Days (DSO)', value: '42 days', target: '< 45 days', good: true },
            { label: 'AP Days (DPO)', value: '28 days', target: '> 30 days', good: false },
            { label: 'Current Ratio', value: '2.57x', target: '> 2.0x', good: true },
            { label: 'Quick Ratio', value: '1.94x', target: '> 1.0x', good: true },
            { label: 'Revenue Growth (YoY)', value: '18%', target: '15%+', good: true },
            { label: 'Net Profit Margin', value: '19.1%', target: '20%', good: false },
          ].map((kpi, i) => (
            <Card key={i} className={`border ${kpi.good ? 'border-success/20 bg-success/5' : 'border-warning/20 bg-warning/5'} hover:-translate-y-1 transition-all`}>
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground font-semibold">{kpi.label}</p>
                <p className={`text-3xl font-semibold mt-2 ${kpi.good ? 'text-success' : 'text-warning'}`}>{kpi.value}</p>
                <p className="text-xs text-muted-foreground mt-2">Target: {kpi.target}</p>
                <div className="mt-2 flex items-center gap-1">
                  <span className={`text-xs font-semibold ${kpi.good ? 'text-success' : 'text-warning'}`}>{kpi.good ? 'On Target' : 'Slightly Below'}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
